import React from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import { InferType } from 'yup'
import { useMutation } from '@tanstack/react-query'
import { Button, ButtonVariant } from '../../common/Button'
import { FormControl } from '../../common/FormControl'
import { HorizontalFormSection } from '../../common/HorizontalFormSection'
import { IconChoices } from '../../common/Icon'
import { TextInput } from '../../common/TextInput'
import { ToastType } from '../../common/Toast/toast-type'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { useFormErrorMessage } from '../../../hooks/useFormErrorMessage'
import { useToastNotifications } from '../../../hooks/useToastNotification'
import { updatePasswordSchema } from '../../../forms/schemas/update_password'
import { updatePassword } from '../../../api/users'

export const ChangePassword = (): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const { getErrorMessage } = useFormErrorMessage()

  const getDefaultValues = (): InferType<typeof updatePasswordSchema> => {
    return {
      currentPassword: '',
      newPassword: '',
    }
  }

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm({
    mode: 'all',
    resolver: yupResolver(updatePasswordSchema),
    defaultValues: getDefaultValues(),
  })

  const { mutate: mutateUpdatePassword, isPending: isUpdatingPassword } =
    useMutation({
      mutationFn: (variables: { newPassword: string; oldPassword: string }) =>
        updatePassword(variables.newPassword, variables.oldPassword),
      onSuccess: () => {
        setToastNotification(
          ToastType.SUCCESS,
          translate('changePassword.flashMessage.updatePasswordSuccess')
        )
        reset()
      },
      onError: () => {
        setToastNotification(
          ToastType.DANGER,
          translate('changePassword.flashMessage.updatePasswordFailure')
        )
      },
    })
  const onSubmit = async (
    data: InferType<typeof updatePasswordSchema>
  ): Promise<void> => {
    mutateUpdatePassword({
      newPassword: data.newPassword,
      oldPassword: data.currentPassword,
    })
  }
  const onError = (): void => {
    setToastNotification(
      ToastType.DANGER,
      translate('flashMessage.somethingWentWrongError')
    )
  }

  const loading = isUpdatingPassword

  return (
    <form onSubmit={handleSubmit(onSubmit, onError)}>
      <HorizontalFormSection
        title={translate('changePassword.title')}
        icon={IconChoices.PASSWORD}
        iconPlacement="left"
        borderBottom
      >
        <Controller
          control={control}
          render={({ field: { value, name, onChange, onBlur } }) => (
            <FormControl
              label={translate('changePassword.labels.currentPassword')}
              error={
                errors.currentPassword &&
                getErrorMessage(errors.currentPassword)
              }
              name={name}
            >
              <TextInput
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                error={!!errors.currentPassword}
                type="password"
              />
            </FormControl>
          )}
          name="currentPassword"
        />
        <Controller
          control={control}
          render={({ field: { value, name, onChange, onBlur } }) => (
            <FormControl
              label={translate('changePassword.labels.newPassword')}
              error={errors.newPassword && getErrorMessage(errors.newPassword)}
              name={name}
            >
              <TextInput
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                error={!!errors.newPassword}
                type="password"
              />
            </FormControl>
          )}
          name="newPassword"
        />
        <div className="mt-4 flex justify-end">
          <Button
            label={translate('common.actions.saveChanges')}
            type="submit"
            loading={loading}
            disabled={loading || !isValid}
            variant={ButtonVariant.PRIMARY}
          />
        </div>
      </HorizontalFormSection>
    </form>
  )
}
